import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { projectDetails } from '../../../../../../../core/projects.selectors';
import { BidService } from '../../../../../../../../../shared/services/bid.service';
import { MaterialService } from '../../../../../../../../../shared/services/material.service';
import { BidItem, BidSheet, Project } from '../../../../../../../../../shared/models';
import { Material } from '../../../../../../../../../shared/models/material';
import { DeliveryTrackingMaterialsDialogComponent } from '../delivery-tracking-materials-dialog/delivery-tracking-materials-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';

@Component({
    selector: 'app-delivery-tracking-bid-items',
    templateUrl: './delivery-tracking-bid-items.component.html',
    styleUrls: ['./delivery-tracking-bid-items.component.scss'],
    standalone: false
})
export class DeliveryTrackingBidItemsComponent implements OnInit, OnDestroy {
  private readonly onDestroy: Subject<void> = new Subject<void>();

  project: Project;
  bidSheet: BidSheet;
  bidItems: BidItem[] = [];
  filteredBidItems: BidItem[] = [];
  materials: Material[] = [];
  searchText = '';
  activeFilter: 'all' | 'enabled' | 'disabled' = 'all';
  filterLabelMap = { all: 'All', enabled: 'Enabled', disabled: 'Not enabled' };
  loading = false;
  selectAllChecked = false;
  selectAllIndeterminate = false;

  displayedColumns: string[] = ['checkbox', 'item', 'description', 'uom', 'materials'];

  totalRecords = 0;
  totalEnabled = 0;
  totalFilteredRecords = 0;
  pagination = { pageSize: 20, pageIndex: 0 };
  currentPage = 1;

  constructor(
    private store: Store<fromRoot.State>,
    private bidService: BidService,
    private materialService: MaterialService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        if (project && Object.keys(project).length) {
          this.project = project;
          this.loadData();
        }
      });
  }

  loadData(): void {
    this.loading = true;
    this.bidService.getBidSheet(this.project.id)
      .toPromise()
      .then((bidSheet: BidSheet) => {
        this.bidSheet = bidSheet;
        return this.fetchBidItems();
      })
      .then(() => this.fetchMaterials())
      .catch(() => {
        this.snackBar.open('Failed to load bid items', '', { duration: 3000 });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  fetchBidItems(): Promise<void> {
    const qp = { include: ['materials'] };
    return this.bidService
      .getBidItems(this.bidSheet, { pageIndex: 0, pageSize: 10000 }, qp)
      .then((resp: any) => {
        this.bidItems = resp.result || [];
        this.totalRecords = this.bidItems.length;
        this.totalEnabled = this.bidItems.filter(bi => bi.track_material_quantity).length;
        this.applyFilter();
      });
  }

  fetchMaterials(): void {
    this.materialService.getRecords(this.project, { pageIndex: 0, pageSize: 1000 })
      .then((resp: any) => {
        this.materials = resp.result || [];
      })
      .catch(() => {
        // Materials fetch is non-critical
      });
  }

  get filterLabel(): string {
    return this.filterLabelMap[this.activeFilter] || 'All';
  }

  onSearch(searchText: string): void {
    this.searchText = searchText || '';
    this.currentPage = 1;
    this.pagination.pageIndex = 0;
    this.applyFilter();
  }

  setFilter(filter: 'all' | 'enabled' | 'disabled'): void {
    this.activeFilter = filter;
    this.currentPage = 1;
    this.pagination.pageIndex = 0;
    this.applyFilter();
  }

  applyFilter(): void {
    let items = [...this.bidItems];

    // Apply enabled/disabled filter
    if (this.activeFilter === 'enabled') {
      items = items.filter(bi => bi.track_material_quantity);
    } else if (this.activeFilter === 'disabled') {
      items = items.filter(bi => !bi.track_material_quantity);
    }

    // Apply search text filter
    if (this.searchText) {
      const term = this.searchText.toLowerCase();
      items = items.filter(bi =>
        bi.item?.toLowerCase().includes(term) ||
        bi.description?.toLowerCase().includes(term)
      );
    }

    this.totalFilteredRecords = items.length;

    // Client-side pagination
    const start = (this.currentPage - 1) * this.pagination.pageSize;
    const end = start + this.pagination.pageSize;
    this.filteredBidItems = items.slice(start, end);

    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    this.selectAllChecked = this.totalEnabled === this.totalRecords && this.totalRecords > 0;
    this.selectAllIndeterminate = this.totalEnabled > 0 && this.totalEnabled < this.totalRecords;
  }

  toggleBidItem(bidItem: BidItem): void {
    const newValue = !bidItem.track_material_quantity;
    this.bidService.bulkUpdateTrackMaterialQuantity({
      ids: [bidItem.id],
      track_material_quantity: newValue,
    })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: () => {
          this.fetchBidItems();
        },
        error: () => {
          this.snackBar.open('Failed to update bid item', '', { duration: 3000 });
        },
      });
  }

  toggleSelectAll(): void {
    const newValue = !this.selectAllChecked;
    const action = newValue ? 'enable' : 'disable';
    const message = `This will ${action} delivery tracking for <b>all ${this.totalRecords} bid items</b> in this project.<br/>Proceed?`;
    const dialogData = new ConfirmDialogModel(`${newValue ? 'Enable' : 'Disable'} all?`, message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.loading = true;
        this.bidService.bulkUpdateTrackMaterialQuantity({
          project_id: this.project.id,
          all: true,
          track_material_quantity: newValue,
        })
          .pipe(takeUntil(this.onDestroy))
          .subscribe({
            next: (result) => {
              this.snackBar.open(`Updated ${result.updated_count} bid items`, '', { duration: 2000 });
              this.fetchBidItems().finally(() => this.loading = false);
            },
            error: () => {
              this.snackBar.open('Failed to update bid items', '', { duration: 3000 });
              this.loading = false;
            },
          });
      });
  }

  openMaterialsDialog(bidItem: BidItem, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DeliveryTrackingMaterialsDialogComponent, {
      width: '420px',
      panelClass: 'materials-dialog-panel',
      data: { bidItem, materials: this.materials, projectId: this.project.id },
    });
    dialogRef.afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe(result => {
        this.fetchBidItems();
      });
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.applyFilter();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
